import { Box, MultiSelect, Portal, Select, Stack, Switch } from '@mantine/core';
import { useSuspenseQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMap } from 'react-map-gl';

import WmsLayer from '../wms-layer/wms-layer';

import classes from './wms-layers-control.module.css';

import { useSharedQueries } from '@data-access';

type SwitchOnChange = React.ComponentProps<typeof Switch>['onChange'];

export const WmsLayersControl = () => {
  const queries = useSharedQueries();
  const layers = useSuspenseQuery(queries.mapLayers());
  const firstLayerId = layers.data?.[0]?.id.toString();
  const [selectedLayers, setSelectedLayers] = useState<string[]>(
    firstLayerId ? [firstLayerId] : [],
  );
  const { t } = useTranslation();
  const [checked, setChecked] = useState(false);
  const map = useMap();
  const mapId = map.current?.getMap().getContainer().id;

  const layersOptions =
    layers.data?.map((layer) => ({
      value: layer.id.toString(),
      label: layer.name,
    })) || [];

  const selectedLayersData = selectedLayers.map((layerId) =>
    layers.data?.find((layer) => layer.id.toString() === layerId),
  );

  const handleChange: SwitchOnChange = (event) => {
    setChecked(event.target.checked);
  };

  if (!mapId) {
    return null;
  }

  return (
    <Portal
      target={
        document &&
        (document
          .getElementById(mapId)
          ?.getElementsByClassName('mapboxgl-ctrl-top-left')[0] as HTMLElement)
      }
    >
      <Stack
        className={clsx(classes.container, 'mapboxgl-ctrl', {
          [classes.blockPadding]: checked,
        })}
      >
        <Switch
          checked={checked}
          label={t('wmsLayerControl.label')}
          onChange={handleChange}
        />
        {checked ? (
          <Box>
            <MultiSelect
              data={layersOptions}
              miw={225}
              onChange={setSelectedLayers}
              value={selectedLayers}
            />
          </Box>
        ) : null}

        {checked
          ? selectedLayersData.map((layer) =>
              layer ? (
                <WmsLayer
                  id={layer.id.toString()}
                  key={layer.id}
                  maxbounds={layer.maxbounds}
                  minZoom={0}
                  url={layer.url}
                />
              ) : null,
            )
          : null}
      </Stack>
    </Portal>
  );
};
